import {
  EMPTY_INPUT_REGEX_MANIFESTATIONS,
  LOGIC_OPERATORS,
  MATCH_OPERATORS,
  REGEX_INDICATORS,
  REGEXES
} from '@/utils/key-regex.constants'

class KeyRegexUtils {
  combineRegexes(regexes, logicOperator) {
    return logicOperator === LOGIC_OPERATORS.AND ?
      this._regexCombineAnd(regexes).source :
      this._regexCombineOr(regexes).source
  }

  _regexCombineAnd(regexes) {
    let combinedStr = '^'
    let end = '[\\s\\S]*$'

    regexes.forEach((regex, i) => {
      const wrapped = `\(?=[\\s\\S]*${regex.source}\)`
      if (i === regexes.length - 1) {
        combinedStr += wrapped
      } else {
        combinedStr += wrapped
      }
    })

    return new RegExp(combinedStr + end, 'm')
  }

  _regexCombineOr(regexes) {
    let combinedStr = ''

    regexes.forEach((regex, i) => {
      const wrapped = `\(${regex.source}\)`
      if (i === regexes.length - 1) {
        combinedStr += wrapped
      } else {
        combinedStr += wrapped + REGEX_INDICATORS.OR_SEPARATOR
      }
    })

    return new RegExp(combinedStr)
  }

  arrayRegexes(items, regexKey = 'regex') {
    return items.map(item => RegExp(item[regexKey]))
  }

  parseRegexToLogic(regex) {
    return regex.includes(REGEX_INDICATORS.AND_INDICATOR) || regex === REGEXES.MATCH_EVERYTHING_WITH.source ?
      LOGIC_OPERATORS.AND :
      LOGIC_OPERATORS.OR
  }

  generateRegexWith(text) {
    if (!text) return REGEXES.MATCH_EVERYTHING_WITH.source

    const encoded = this._escapeRegexString(text)
    return `^.*${encoded}.*$`
  }

  generateRegexWithout(text) {
    if (!text) return REGEXES.MATCH_EVERYTHING_WITHOUT.source

    const encoded = this._escapeRegexString(text)
    return `^((?!${encoded}).)*$`
  }

  _escapeRegexString(text) {
    const regex = /([/?:.*+-])/g
    return text.replace(regex, '\\$1')
  }

  _unescapeRegexString(text) {
    const regex = /\\([/?:.*+-])/g
    return text.replace(regex, '$1')
  }

  parseRegexToOperands(regex, logicOperator) {
    const splitter = logicOperator === LOGIC_OPERATORS.AND ?
      REGEX_INDICATORS.AND_SEPARATOR :
      REGEX_INDICATORS.OR_SEPARATOR

    return regex.split(splitter).map(item => {
      const containsWithout = item.includes(REGEX_INDICATORS.WITHOUT_INDICATOR)
      // is kind of hack: REGEX_INDICATORS.EMPTY_WITHOUT_INDICATOR === item
      // to secure indication of with/without operator for empty strings
      const matchOperator = containsWithout || REGEX_INDICATORS.EMPTY_WITHOUT_INDICATOR === item ?
        MATCH_OPERATORS.WITHOUT :
        MATCH_OPERATORS.WITH
      const text = this._unescapeRegexString(this._sanitizeRegexString(item, matchOperator))
      const regex = containsWithout ? this.generateRegexWithout(text) : this.generateRegexWith(text)

      return { matchOperator, text, regex }
    })
  }

  _sanitizeRegexString(string, matchOperator) {
    let stringStartIndex, stringEndIndex, stringSeparator

    stringSeparator = matchOperator === MATCH_OPERATORS.WITH ?
      REGEX_INDICATORS.WITH_TEXT_START_SEPARATOR :
      REGEX_INDICATORS.WITHOUT_INDICATOR

    stringStartIndex = string.indexOf(stringSeparator)
    stringEndIndex = string.lastIndexOf(
      matchOperator === MATCH_OPERATORS.WITH ? stringSeparator : REGEX_INDICATORS.WITHOUT_TEXT_END_SEPARATOR
    )

    const sanitized = string.substring(stringStartIndex + stringSeparator.length, stringEndIndex)

    return EMPTY_INPUT_REGEX_MANIFESTATIONS.includes(sanitized) ? '' : sanitized
  }

  buildBrowserUrlLabel(regex) {
    const logicOperator = this.parseRegexToLogic(regex)
    const operands = this.parseRegexToOperands(regex, logicOperator)
    const operandsWith = operands.filter(({ matchOperator }) => matchOperator === MATCH_OPERATORS.WITH)
    const operandsWithout = operands.filter(({ matchOperator }) => matchOperator === MATCH_OPERATORS.WITHOUT)
    const labelWith = this._buildBrowserUrlLabel(operandsWith)
    const labelWithout = this._buildBrowserUrlLabel(operandsWithout, MATCH_OPERATORS.WITHOUT)

    return this._unescapeRegexString(`${labelWith} ${labelWithout}`.trim())
  }

  _buildBrowserUrlLabel(operands, prefix = MATCH_OPERATORS.WITH) {
    const label = operands.map(({ text }, i) => {
      const operandLabel = `"${text || ' '}";`
      if (i === 0) return `${prefix} ${operandLabel}`

      return operandLabel
    })

    return label.join(' ')
  }

  isEmptyStringRegex(regex) {
    return (
      // for not combined regexes
      regex.includes(REGEXES.MATCH_EVERYTHING_WITH.source) ||
      regex.includes(REGEXES.MATCH_EVERYTHING_WITHOUT.source) ||
      // for combined regexes
      regex.includes(REGEX_INDICATORS.EMPTY_WITHOUT_INDICATOR) ||
      regex.includes(REGEX_INDICATORS.EMPTY_WITH_INDICATOR)
    )
  }

  isRegexValid(regex) {
    try {
      new RegExp(regex)
      return true
    } catch {
      return false
    }
  }
}

export default new KeyRegexUtils()
